import { Box, TextInput, type TextInputProps } from "@mantine/core";
import { IconAlertOctagon } from "@tabler/icons-react";
import clsx from "clsx";
import { useId, type Ref } from "react";
import type { UseFormRegisterReturn } from "react-hook-form";
import styles from "./Input.module.scss";

export type InputSize = "small" | "large";

export type InputProps = Omit<
  TextInputProps,
  "value" | "onChange" | "error" | "type" | "placeholder" | "disabled"
> & {
  label?: string;
  optional?: boolean;
  required?: boolean;
  hint?: string;
  helpIcon?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  size?: InputSize;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
  value?: string;
  onChange?: (val: string) => void;
  error?: boolean | string;
  type?: string;
  register?: UseFormRegisterReturn;
  name?: string;
  className?: string;
  style?: React.CSSProperties;
  dataTestId?: string;
  width?: string | number; // Input wrapper width
  inputElementWidth?: string | number; // Input html element width
  autoComplete?: string;
  step?: string | number; // Step attribute for number inputs
  inputRef?: Ref<HTMLInputElement>;
  wrapperId?: string;
};

export function Input({
  label,
  optional,
  required,
  hint,
  helpIcon,
  placeholder = "Enter",
  disabled,
  size = "small",
  iconLeft,
  iconRight,
  value,
  onChange,
  error,
  register,
  name,
  type,
  className,
  style,
  dataTestId,
  width,
  inputElementWidth = "100%",
  autoComplete,
  step,
  wrapperId,
  ...rest
}: InputProps) {
  const id = useId();
  /*
   * SERV-2649 (NFR5): the error text is tied to the input for assistive technology, not only shown
   * beside it. Mantine owns `aria-invalid` and `aria-describedby` (its Input spreads them after
   * every prop passed in), so `aria-invalid` comes from handing Mantine a boolean `error` with its
   * error styles off - the look stays this component's own - and the message is linked through
   * `aria-errormessage`, which Mantine leaves alone.
   */
  const errorId = `${id}-error`;
  const hasErrorMessage = typeof error === "string" && error !== "";

  const iconSize = size === "large" ? 24 : 16;
  const leftWidth = 16 + iconSize;
  const rightWidth = iconRight ? 16 + iconSize : undefined;

  const mergedRegister =
    register &&
    ({
      ...register,
      ref: (el: HTMLInputElement | null) => {
        if (typeof register.ref === "function") {
          register.ref(el);
        } else if (register.ref && typeof register.ref === "object") {
          (register.ref as any).current = el;
        }
        if (typeof (rest as any).inputRef === "function") {
          (rest as any).inputRef(el);
        } else if ((rest as any).inputRef && typeof (rest as any).inputRef === "object") {
          ((rest as any).inputRef as any).current = el;
        }
      },
    } as UseFormRegisterReturn);

  return (
    <Box
      id={wrapperId}
      className={clsx(styles.wrapper, styles[`wrapper-${size}`], className)}
      w={width}
      style={{ minWidth: style?.minWidth, maxWidth: style?.maxWidth }}
    >
      {(label || optional) && (
        <Box className={styles.labelRow}>
          {label && (
            <label htmlFor={id} className={styles.labelText}>
              {label}
              {required && <span style={{ color: "var(--text-danger-default)" }}> *</span>}
              {helpIcon && <span className={styles.helpIcon}>{helpIcon}</span>}
            </label>
          )}
          {optional && <span className={styles.optionalText}>Optional</span>}
        </Box>
      )}

      <TextInput
        data-testid={dataTestId}
        id={id}
        name={name || register?.name}
        value={value}
        onChange={(e) => onChange?.(e.currentTarget.value)}
        {...(mergedRegister || register)}
        step={step}
        type={type}
        onKeyDown={(e) => {
          if (type === "number" && (e.key === "e" || e.key === "E")) {
            e.preventDefault();
          }
        }}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        leftSection={iconLeft}
        leftSectionWidth={iconLeft ? leftWidth : undefined}
        rightSection={iconRight}
        rightSectionWidth={iconRight ? rightWidth : undefined}
        style={style}
        classNames={{
          input: clsx(styles.input, styles[`input-${size}`], error && styles.inputError),
        }}
        w={inputElementWidth}
        error={Boolean(error)}
        withErrorStyles={false}
        aria-errormessage={hasErrorMessage ? errorId : undefined}
        onWheel={
          type === "number"
            ? (e: React.WheelEvent<HTMLInputElement>) => {
                // Prevent the input value change
                (e.target as HTMLInputElement).blur();

                // Prevent the page/container scrolling
                e.stopPropagation();

                // Refocus the input
                setTimeout(() => {
                  (e.target as HTMLInputElement).focus();
                }, 0);
              }
            : undefined
        }
        {...rest}
      />

      {hasErrorMessage && (
        <Box id={errorId} className={styles.errorMessage}>
          <IconAlertOctagon size={16} className={styles.errorIcon} />
          <span>{error}</span>
        </Box>
      )}

      {hint && <Box className={styles.hint}>{hint}</Box>}
    </Box>
  );
}
